import type { ITalkCallsEvent, ITalkCallsPresenceEntry, ITalkCallsPresenceTargetType } from '@zyno-platform/shared';

import { computed, onMounted, onUnmounted, reactive } from 'vue';

import { SSETalkCallsChannel } from '@/modules/talk/sse';
import { TalkPresenceApi } from '@/openapi-clients-generated/talk';
import { Logger } from '@/shared/services/logger';

const log = Logger.createScoped('Presence');

/** The `presence-update` delta payload, derived from the shared SSE union so the two never drift. */
type PresenceDelta = Extract<ITalkCallsEvent, { event: 'presence-update' }>['payload'];

export type ExtensionPresenceState = 'ringing' | 'on-call' | 'held';
export type PresenceTargetType = ITalkCallsPresenceTargetType;

export interface ExtensionPresence {
    state: ExtensionPresenceState;
    /** Source timestamp for the current aggregate state — for a live "since" timer. */
    since: number;
    /** Live call ids for this extension. */
    callIds: string[];
    callCount: number;
}

export interface PresenceTarget extends ExtensionPresence {
    targetId: string;
    targetType: PresenceTargetType;
    extensionId?: string;
    parkingSlotExtension?: string;
}

interface StoredPresence extends PresenceTarget {
    ts: number;
}

/**
 * Tenant presence consumer (busy-lamp-field). Subscribes to the tenant `talk-calls` SSE channel,
 * filters `presence-update` deltas, and reconciles them against the REST `GET /presence` snapshot.
 *
 * Reconcile is race-safe by construction: we subscribe FIRST (buffering deltas), THEN read the
 * snapshot, THEN replay the buffer — so a delta that lands during the fetch is never lost. Conflicts
 * resolve last-writer-wins per `targetId` by `ts`, which also defeats out-of-order NATS redelivery.
 * An `idle` delta removes the target.
 *
 * The feed is reference-counted via `use()`, so the snapshot + subscription are only live while a
 * consumer (e.g. the dialer's directory pane) is mounted.
 */
class PresenceManager {
    private readonly targets = reactive(new Map<string, StoredPresence>());
    private readonly lastTs = new Map<string, number>();

    /** Deltas that arrive between subscribe and snapshot-apply; replayed (by ts) once the snapshot lands. */
    private buffer: PresenceDelta[] = [];
    private buffering = false;
    private refCount = 0;

    private readonly listener = (data: ITalkCallsEvent) => {
        if (data?.event !== 'presence-update') return;
        if (this.buffering) {
            this.buffer.push(data.payload);
            return;
        }
        this.applyDelta(data.payload);
    };

    /** Per-BLF-target aggregate; an absent target id is idle. */
    readonly byTarget = computed(() => {
        const map = new Map<string, PresenceTarget>();
        for (const [targetId, presence] of this.targets) {
            map.set(targetId, {
                targetId: presence.targetId,
                targetType: presence.targetType,
                extensionId: presence.extensionId,
                parkingSlotExtension: presence.parkingSlotExtension,
                state: presence.state,
                since: presence.since,
                callIds: presence.callIds,
                callCount: presence.callCount
            });
        }
        return map;
    });

    /** Per-extension aggregate; parking-slot targets are excluded. */
    readonly byExtension = computed(() => {
        const map = new Map<string, ExtensionPresence>();
        for (const presence of this.byTarget.value.values()) {
            if (presence.targetType !== 'extension' || !presence.extensionId) continue;
            map.set(presence.extensionId, {
                state: presence.state,
                since: presence.since,
                callIds: presence.callIds,
                callCount: presence.callCount
            });
        }
        return map;
    });

    /** Per-parking-slot aggregate keyed by visible slot extension, e.g. `701`. */
    readonly byParkingSlot = computed(() => {
        const map = new Map<string, PresenceTarget>();
        for (const presence of this.byTarget.value.values()) {
            if (presence.targetType !== 'parking-slot' || !presence.parkingSlotExtension) continue;
            map.set(presence.parkingSlotExtension, presence);
        }
        return map;
    });

    presenceFor(extensionId: string): ExtensionPresence | undefined {
        return this.byExtension.value.get(extensionId);
    }

    presenceForTarget(targetId: string): PresenceTarget | undefined {
        return this.byTarget.value.get(targetId);
    }

    presenceForParkingSlot(slotExtension: string): PresenceTarget | undefined {
        return this.byParkingSlot.value.get(slotExtension);
    }

    /** Hold the live feed open for the lifetime of the calling component. */
    use() {
        onMounted(() => this.acquire());
        onUnmounted(() => this.release());
    }

    private acquire() {
        this.refCount += 1;
        if (this.refCount > 1) return;
        SSETalkCallsChannel.subscribe(this.listener);
        void this.reconcile();
    }

    private release() {
        this.refCount = Math.max(0, this.refCount - 1);
        if (this.refCount > 0) return;
        SSETalkCallsChannel.unsubscribe(this.listener);
        this.buffer = [];
        this.buffering = false;
        this.targets.clear();
        this.lastTs.clear();
    }

    /** Reset to the REST snapshot, then replay deltas that landed during the fetch (last-writer-wins). */
    private async reconcile() {
        this.buffering = true;
        this.buffer = [];
        const snapshotStartedAt = Date.now();

        let entries: ITalkCallsPresenceEntry[] = [];
        try {
            const { data } = await TalkPresenceApi.getPresenceSnapshot();
            entries = (data?.entries ?? []) as unknown as ITalkCallsPresenceEntry[];
        } catch (err) {
            log.warn('presence snapshot failed; relying on deltas', err);
        }

        // Released while the snapshot was in flight — drop everything, stay clean for the next open.
        if (this.refCount === 0) {
            this.buffer = [];
            this.buffering = false;
            return;
        }

        this.targets.clear();
        this.lastTs.clear();
        for (const e of entries) {
            this.lastTs.set(e.targetId, snapshotStartedAt);
            this.targets.set(e.targetId, {
                targetId: e.targetId,
                targetType: e.targetType,
                extensionId: e.extensionId,
                parkingSlotExtension: e.parkingSlotExtension,
                state: e.state,
                since: e.since,
                callIds: e.callIds,
                callCount: e.callIds.length,
                ts: snapshotStartedAt
            });
        }

        const buffered = this.buffer;
        this.buffer = [];
        this.buffering = false;
        for (const p of buffered.sort((a, b) => a.ts - b.ts)) this.applyDelta(p);
    }

    private applyDelta(p: PresenceDelta) {
        const last = this.lastTs.get(p.targetId);
        if (last != null && last >= p.ts) return; // stale / out-of-order redelivery
        this.lastTs.set(p.targetId, p.ts);

        if (p.state === 'idle') {
            this.targets.delete(p.targetId);
            return;
        }
        this.targets.set(p.targetId, {
            targetId: p.targetId,
            targetType: p.targetType,
            extensionId: p.extensionId,
            parkingSlotExtension: p.parkingSlotExtension,
            state: p.state,
            since: p.since ?? p.ts,
            callIds: p.callIds,
            callCount: p.callIds.length,
            ts: p.ts
        });
    }
}

export const presenceMgr = new PresenceManager();
